import React, { useCallback, useEffect, useMemo, useState } from "react";
import RequestV2 from "../../../api/RequestV2";
import { toast } from "react-toastify";
import Config from "../../../utils/config";
import { CsvDown } from "../../integrations/export";
import {
    PageBodyTitle,
    Card,
    Table,
    PageLoader,
    ListPageFilterBar,
    StatusBadge,
    Button,
} from "../../design-system";
import { theme } from "../../design-system/theme";
import styled from "styled-components";
import { Link } from "react-router-dom";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    TextField,
    Menu,
    MenuItem,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const FILTER_INITIAL = [
    {
        name: "mandateId",
        type: "text",
        value: "",
        placeholder: "AutoPay ID",
    },
    {
        name: "mobileNumber",
        type: "text",
        value: "",
        placeholder: "Mobile Number",
    },
    {
        name: "customerName",
        type: "text",
        value: "",
        placeholder: "Customer Name",
    },
    {
        name: "status",
        type: "select",
        value: "",
        placeholder: "Status",
        options: [
            { label: "Pending", value: "PENDING" },
            { label: "Approved", value: "APPROVED" },
            { label: "Rejected", value: "REJECTED" },
        ],
    },
    {
        name: "raisedBy",
        type: "select",
        value: "",
        placeholder: "Raised By",
        options: [
            { label: "Customer", value: "CUSTOMER" },
            { label: "Agent", value: "AGENT" },
        ],
    },
];
const bp = theme.breakpoints || { sm: "640px" };
const PageWrapper = styled.div`
  max-width: 1440px;
  width: 100%;
  margin: 0 auto;
  padding: ${theme.spacing.sm} 0 0;
`;
const TopBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-start;
  gap: ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.lg};
  width: 100%;
  @media (max-width: ${bp.sm}) {
    flex-direction: column;
    align-items: flex-start;
  }
`;
const ActionsRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  margin-left: auto;
`;
const CSV_HEADERS = [
    { label: "AutoPay ID", key: "mandateId" },
    { label: "Customer Name", key: "customerName" },
    { label: "Mobile Number", key: "mobileNumber" },
    { label: "Request Remark", key: "requestRemark" },
    { label: "Admin Remark", key: "adminRemark" },
    { label: "Status", key: "status" },
    { label: "Raised By", key: "raisedBy" },
    { label: "Created At", key: "createdAt" },
    { label: "Updated At", key: "updatedAt" },
];
const WrapText = styled.div`
    white-space: wrap;
    word-break: break-all;
    max-width: 200px;
`;

export default function RevokeMandateList() {
    const [loading, setLoading] = useState(false);
    const [controls, setControls] = useState([...FILTER_INITIAL]);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [mandateList, setMandateList] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [downloadPayload, setDownloadPayload] = useState({});
    const [remarks, setRemarks] = useState("");
    const [userDetailModal, setUserDetailModal] = useState({ open: false, loading: false, data: null, });
    const [actionModal, setActionModal] = useState({ open: false, row: null, status: "", });
    const openActionModal = (row, status) => { setActionModal({ open: true, row, status, }); };
    const closeModal = () => {
        setActionModal({ open: false, row: null, status: "", });
        setRemarks("");
    };
    const fetchList = useCallback((filterControls = controls) => {
        setLoading(true);
        const filterValues = Object.fromEntries(
            filterControls.map(({ name, value }) => [name, value])
        );
        const payload = {
            page: currentPage - 1,
            size: pageSize,
            ...Object.fromEntries(
                Object.entries(filterValues).filter(([, value]) => value)
            ),
        };
        setDownloadPayload(payload);
        const api = new RequestV2(
            "",
            (res) => {
                setMandateList(res.content || []);
                setTotalRecords(res.totalElements || 0);
                setLoading(false);
            },
            (err) => {
                setLoading(false);
                const response = err?.response?.data || err?.data || err;
                if (response?.fieldErrors?.length) {
                    response.fieldErrors.forEach((item) => {
                        toast.error(item.message);
                    });
                    return;
                }
                toast.error(response?.message || "Something went wrong");
            }
        );
        api.post(Config.apis.user.REVOKE_MANDATE_LIST, payload);
    }, [currentPage, pageSize, controls]);

    useEffect(() => {
        fetchList();
    }, [currentPage, pageSize]);

    const handleFilterChange = (name, value) => {
        setControls((prev) => prev.map((c) => (c.name === name ? { ...c, value } : c)));
    };

    const fetchUserDetails = (userId) => {
        setUserDetailModal({ open: true, loading: true, data: null, });
        const api = new RequestV2(
            "",
            (res) => {
                setUserDetailModal({ open: true, loading: false, data: res, });
            },
            (err) => {
                setUserDetailModal({ open: false, loading: false, data: null, });
                toast.error(
                    err?.response?.data?.message ||
                    err?.message ||
                    "Failed to fetch user details"
                );
            }
        );
        api.get(`${Config.apis.user.FETCH_USER_DETAILS}/${userId}`);
    };

    const onSearch = () => {
        setCurrentPage(1);
        fetchList(controls);
    };

    const onReset = () => {
        const resetControls = FILTER_INITIAL.map((item) => ({ ...item }));
        setControls(resetControls);
        setCurrentPage(1);
        fetchList(resetControls);
    };

    const formatDate = (date) => date ? new Date(date).toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
    }) : "—";

    const columns = [
        {
            key: "mandateId",
            label: "AutoPay ID",
            render: (_, row) => (
                <Link
                    to={`/loan-repayment-autopay/${row.mandateId}`}
                    state={{
                        userName: row.customerName,
                        mobileNumber: row.mobileNumber,
                        fromRevokeMandate: true,
                        requestId: row.requestId,
                        revokeStatus: row.status,
                    }}
                    style={{ textDecoration: "underline" }}
                >
                    {row.mandateId || "—"}
                </Link>
            ),
        },
        {
            key: "customerName",
            label: "Customer Name",
            render: (_, row) => row.customerName || "—",
        },
        {
            key: "mobileNumber",
            label: "Mobile Number",
            render: (_, row) => row.mobileNumber || "—",
        },
        {
            key: "requestRemark",
            label: "Request Remark",
            render: (_, row) => (<WrapText>{row.requestRemark || "-"}</WrapText>)
        },
        {
            key: "adminRemark",
            label: "Admin Remark",
            render: (_, row) => (<WrapText>{row.adminRemark || "-"}</WrapText>)
        },
        {
            key: "status",
            label: "Status",
            render: (_, row) =>
                row.status ? <StatusBadge status={row.status} /> : "—",
        },
        {
            key: "raisedBy",
            label: "Raised By",
            render: (_, row) => (
                <Button
                    variant="outline"
                    style={{ border: "none", padding: "5px", fontSize: "12px" }}
                    onClick={() => fetchUserDetails(row.createdBy)}
                >
                    {row.raisedBy || "—"}
                </Button>
            )
        },
        {
            key: "createdAt",
            label: "Created At",
            render: (_, row) => formatDate(row.createdAt),
        },
        {
            key: "updatedAt",
            label: "Updated At",
            render: (_, row) => formatDate(row.updatedAt),
        },
        {
            key: "action",
            label: "Action",
            render: (_, row) =>
                row.status === "PENDING" ? (
                    <RevokeActionCell
                        row={row}
                        onAction={openActionModal}
                    />
                ) : (
                    "—"
                ),
        },
    ];

    function RevokeActionCell({ row, onAction }) {
        const [anchor, setAnchor] = useState(null);
        const close = () => setAnchor(null);
        return (
            <>
                <Button type="button" variant="outline" endIcon={<ExpandMoreIcon />}
                    onClick={(e) => setAnchor(e.currentTarget)}
                    style={{ padding: "5px", minHeight: "25px" }}
                >
                    Action
                </Button>
                <Menu
                    anchorEl={anchor}
                    open={Boolean(anchor)}
                    onClose={close}
                >
                    <MenuItem
                        sx={{
                            color: "#16A34A",
                            fontWeight: 500,
                            fontSize: 12,
                            "&:hover": {
                                backgroundColor: "#DCFCE7",
                            },
                        }}
                        onClick={() => {
                            close();
                            onAction(row, "APPROVED");
                        }}
                    >
                        Approve
                    </MenuItem>
                    <MenuItem
                        sx={{
                            color: "#DC2626",
                            fontWeight: 500,
                            fontSize: 12,
                            "&:hover": {
                                backgroundColor: "#FEE2E2",
                            },
                        }}
                        onClick={() => {
                            close();
                            onAction(row, "REJECTED");
                        }}
                    >
                        Reject
                    </MenuItem>
                </Menu>
            </>
        );
    }

    const submitAction = () => {
        const trimmedRemarks = remarks.trim();
        const api = new RequestV2(
            "",
            (res) => {
                toast.success(res?.data || res?.message || "Success");
                closeModal();
                fetchList();
            },
            (err) => {
                toast.error(
                    err?.response?.data?.message ||
                    err?.message ||
                    "Something went wrong"
                );
            }
        );
        api.post(
            Config.apis.user.ACTION_REVOKE_MANDATE,
            {
                requestId: actionModal.row.requestId,
                status: actionModal.status,
                remarks: trimmedRemarks,
            }
        );
    };

    const filterConfig = useMemo(() => controls, [controls]);
    if (loading && mandateList.length === 0) {
        return <PageLoader />;
    }

    return (
        <PageWrapper>
            <TopBar>
                <PageBodyTitle>Loan Repayment AutoPay</PageBodyTitle>
                <ActionsRow>
                    <CsvDown
                        headers={CSV_HEADERS}
                        url={`${Config.apis.user.REVOKE_MANDATE_LIST}?page=0&size=${totalRecords || 9999}`}
                        params={downloadPayload}
                        method="post"
                        title="CSV"
                        reportName="revoke-autopay-list.csv"
                        useV2
                    />
                </ActionsRow>
            </TopBar>
            <Card style={{ marginTop: theme.spacing.md }}>
                <ListPageFilterBar
                    filters={filterConfig}
                    onFilterChange={handleFilterChange}
                    onSearch={onSearch}
                    onReset={onReset}
                />
                {loading ? (
                    <PageLoader />
                ) : (
                    <Table
                        data={mandateList}
                        columns={columns}
                        rowKey={(row) => row.requestId}
                        emptyMessage="No record found"
                        pagination={{
                            currentPage,
                            pageSize,
                            total: totalRecords,
                            onPageChange: setCurrentPage,
                            pageSizeOptions: [10, 20, 50],
                            onPageSizeChange: (size) => {
                                setPageSize(size);
                                setCurrentPage(1);
                            },
                        }}
                    />
                )}
            </Card>
            <Dialog
                open={actionModal.open}
                onClose={closeModal}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    {actionModal.status === "APPROVED" ? "Approve Revoke Request" : "Reject Revoke Request"}
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ mb: 2 }}>
                        {actionModal.status === "APPROVED"
                            ? "Are you sure you want to approve this revoke request?"
                            : "Are you sure you want to reject this revoke request?"}
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        label="Remarks*"
                        placeholder="Enter remarks"
                        value={remarks}
                        inputProps={{ maxLength: 150 }}
                        helperText={`${remarks.length}/150`}
                        onChange={(e) => setRemarks(e.target.value)}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        color="error"
                        onClick={closeModal}
                        variant="outline"
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color={actionModal.status === "APPROVED" ? "success" : "error"}
                        disabled={!remarks.trim()}
                        onClick={submitAction}
                    >
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={userDetailModal.open}
                onClose={() => setUserDetailModal({ open: false, loading: false, data: null, })}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Raised By</DialogTitle>
                <DialogContent>
                    {userDetailModal.loading ? (
                        <PageLoader />
                    ) : (
                        <>
                            <Typography>
                                <strong>First Name:</strong>{" "}
                                {userDetailModal.data?.firstName || "-"}
                            </Typography>
                            {userDetailModal.data?.lastName ?
                                <Typography>
                                    <strong>Last Name:</strong>{" "}
                                    {userDetailModal.data?.lastName || "-"}
                                </Typography>
                                : ""}
                            <Typography>
                                <strong>Mobile Number:</strong>{" "}
                                {userDetailModal.data?.mobile || "-"}
                            </Typography>
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="outline"
                        onClick={() => setUserDetailModal({ open: false, loading: false, data: null, })}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </PageWrapper>
    );
}
